import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { gql, useQuery, useMutation } from '@apollo/client';

const GET_AVAILABLE_PACKAGES = gql`
  query GetAvailablePackages {
    availablePackages {
      id
      name
      price
      currency
      discount_percentage
      details
      notes
      duration_days
      max_staff_members
      max_contacts
      max_whatsapp_instances
      max_contact_groups
      max_templates
      max_bulk_messages_per_day
      priority_support
      features
      country
      package_type
      is_active
      created_at
      updated_at
      terms_and_conditions
    }
  }
`;

const GET_CURRENT_SUBSCRIPTION = gql`
  query GetCurrentSubscription {
    mySubscriptions {
      id
      status
      expires_at
      package {
        id
        name
        price
      }
    }
  }
`;

const CREATE_ORDER = gql`
  mutation CreateOrder($input: CreateOrderInput!) {
    createOrder(input: $input) {
      success
      message
      order {
        id
        amount
        currency
        status
      }
      razorpay_order {
        id
        amount
        currency
        status
      }
    }
  }
`;

const VERIFY_PAYMENT = gql`
  mutation VerifyPayment($input: VerifyPaymentInput!) {
    verifyPayment(input: $input) {
      success
      message
      subscription {
        id
        status
        expires_at
      }
    }
  }
`;

const CANCEL_ORDER = gql`
  mutation CancelOrder($order_id: ID!) {
    cancelOrder(order_id: $order_id) {
      success
      message
      order {
        id
        status
      }
    }
  }
`;

const SubscriptionPlans = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useSelector((state) => state.auth);
  const [currentStep, setCurrentStep] = useState('welcome'); // 'welcome' or 'plans'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showPackageDetails, setShowPackageDetails] = useState(false);
  const [selectedPackageForDetails, setSelectedPackageForDetails] = useState(null);

  // Check email verification when modal opens
  useEffect(() => {
    if (isOpen && !user?.email_verified) {
      // Close this modal and let parent handle email verification
      onClose();
      return;
    }
  }, [isOpen, user?.email_verified, onClose]);

  const { data: packagesData, loading: packagesLoading, error: packagesError } = useQuery(GET_AVAILABLE_PACKAGES, {
    skip: !isOpen || currentStep === 'welcome',
    fetchPolicy: 'cache-and-network'
  });

  const { data: currentSubscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, {
    skip: !isOpen,
    fetchPolicy: 'cache-and-network'
  });

  const [createOrder] = useMutation(CREATE_ORDER);
  const [verifyPayment] = useMutation(VERIFY_PAYMENT);
  const [cancelOrder] = useMutation(CANCEL_ORDER);

  // Reset to welcome step when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('welcome');
      setError('');
      setIsProcessing(false);
    }
  }, [isOpen]);

  // Load Razorpay script
  useEffect(() => {
    if (isOpen && !window.Razorpay) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [isOpen]);

  const handleMaybeLater = () => {
    onClose(); // Just close, will show again on refresh
  };

  const handleDontShowAgain = () => {
    // Permanently dismiss until user manually opens subscription
    localStorage.setItem('subscription_permanently_dismissed', 'true');
    onClose();
  };

  const handlePlanSelect = async (packageData) => {
    if (isProcessing) return;

    setIsProcessing(true);
    setError('');
    setSelectedPlan(packageData);

    try {
      console.log('🔄 Creating order for package:', packageData.id);

      const { data: orderData } = await createOrder({
        variables: {
          input: {
            package_id: parseInt(packageData.id),
            notes: {
              selected_from: 'subscription_popup',
              package_name: packageData.name
            }
          }
        }
      });

      if (!orderData?.createOrder?.success) {
        throw new Error(orderData?.createOrder?.message || 'Failed to create order');
      }

      const { order, razorpay_order } = orderData.createOrder;
      console.log('✅ Order created:', order);

      // Initialize Razorpay payment
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_RD6hnhgLR78IQe',
        amount: razorpay_order.amount,
        currency: razorpay_order.currency || 'INR',
        name: 'Bulky Marketing',
        description: `${packageData.name} Subscription`,
        order_id: razorpay_order.id,
        handler: async function (response) {
          console.log('💳 Payment response:', response);

          try {
            const { data: verifyData } = await verifyPayment({
              variables: {
                input: {
                  order_id: parseInt(order.id),
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature
                }
              }
            });

            if (verifyData?.verifyPayment?.success) {
              console.log('✅ Payment verified successfully');
              onSuccess?.(verifyData.verifyPayment.subscription);
              onClose();
            } else {
              throw new Error(verifyData?.verifyPayment?.message || 'Payment verification failed');
            }
          } catch (verifyError) {
            console.error('❌ Payment verification error:', verifyError);
            setError('Payment verification failed. Please contact support.');
          } finally {
            setIsProcessing(false);
            setSelectedPlan(null);
          }
        },
        prefill: {
          name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.username,
          email: user?.email,
          contact: user?.mobile?.replace(/^\+/, '') || ''
        },
        theme: {
          color: '#3B82F6'
        },
        modal: {
          ondismiss: async function () {
            console.log('💳 Payment cancelled by user');
            
            // Cancel the order in the database
            try {
              await cancelOrder({
                variables: {
                  order_id: parseInt(order.id)
                }
              });
              console.log('✅ Order cancelled successfully');
            } catch (cancelError) {
              console.error('❌ Failed to cancel order:', cancelError);
            }
            
            setIsProcessing(false);
            setSelectedPlan(null);
          }
        },
        retry: {
          enabled: true,
          max_count: 3
        },
        timeout: 300
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on('payment.failed', async (response) => {
        console.error('❌ Payment failed:', response.error);
        
        // Cancel the order in the database
        try {
          await cancelOrder({
            variables: {
              order_id: parseInt(order.id)
            }
          });
          console.log('✅ Order cancelled after payment failure');
        } catch (cancelError) {
          console.error('❌ Failed to cancel order:', cancelError);
        }
        
        setError(`Payment failed: ${response.error.description}`);
        setIsProcessing(false);
        setSelectedPlan(null);
      });

      razorpay.open();

    } catch (error) {
      console.error('❌ Order creation error:', error);
      setError(error.message || 'Failed to initiate payment');
      setIsProcessing(false);
      setSelectedPlan(null);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay">
      <div className="modal-container max-w-4xl">
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white">
              <span className="material-icons-round text-sm">{currentStep === 'welcome' ? 'rocket_launch' : 'explore'}</span>
            </div>
            <div>
              <h2 className="text-xl font-medium text-white tracking-tight">
                {currentStep === 'welcome' ? 'Get Started' : 'Purchase Plan'}
              </h2>
              <p className="text-[10px] text-white/70 font-normal uppercase tracking-wider leading-none mt-0.5">
                {currentStep === 'welcome' ? 'Choose Your Plan' : 'Choose Your Subscription Plan'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 hover:bg-white/20 text-white rounded-lg flex items-center justify-center transition-all"
          >
            <span className="material-icons-round text-sm">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="modal-content !p-0">
          {currentStep === 'welcome' ? (
            /* Welcome Step */
            <div className="p-6 text-center bg-gradient-to-b from-blue-50/50 to-white">
              <div className="w-16 h-16 bg-bulky-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl shadow-bulky-blue-500/20">
                <span className="material-icons-round text-white text-3xl">rocket_launch</span>
              </div>

              <h2 className="text-xl font-medium text-bulky-navy-800 tracking-tight">Get Started with Bulky</h2>
              <p className="text-[10px] font-normal text-gray-400 uppercase tracking-[0.2em] mb-6 max-w-sm mx-auto leading-relaxed">
                To use all features of Bulky Marketing, you need to purchase a subscription plan.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-all">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="material-icons-round text-blue-500 text-lg">groups</span>
                  </div>
                  <h2 className="text-xs font-medium text-bulky-navy-800 uppercase tracking-wider leading-none">Staff Management</h2>
                  <p className="text-[8px] text-gray-400 font-normal uppercase leading-tight">Manage Your Team</p>
                </div>

                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-all">
                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="material-icons-round text-emerald-500 text-lg">contacts</span>
                  </div>
                  <h2 className="text-xs font-medium text-bulky-navy-800 uppercase tracking-wider leading-none">Contact Storage</h2>
                  <p className="text-[8px] text-gray-400 font-normal uppercase leading-tight">Save Customer Contacts</p>
                </div>

                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-all">
                  <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="material-icons-round text-orange-500 text-lg">message</span>
                  </div>
                  <h2 className="text-xs font-medium text-bulky-navy-800 uppercase tracking-wider leading-none">Bulk Messaging</h2>
                  <p className="text-[8px] text-gray-400 font-normal uppercase leading-tight">Send Messages to Many</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 justify-center border-t border-gray-50 pt-6">
                <button
                  onClick={() => setCurrentStep('plans')}
                  className="btn-primary py-2.5 px-6 !text-[10px]"
                >
                  <span className="material-icons-round text-sm">shopping_cart</span>
                  <span>View Plans</span>
                </button>

                <button
                  onClick={handleMaybeLater}
                  className="btn-secondary py-2.5 px-6 !text-[10px]"
                >
                  Maybe Later
                </button>
              </div>

              {/* Don't show again option */}
              <div className="mt-6">
                <button
                  onClick={handleDontShowAgain}
                  className="text-[9px] font-normal text-gray-400 hover:text-bulky-navy-400 uppercase tracking-[0.2em] transition-colors"
                >
                  Don't Show Again
                </button>
              </div>
            </div>
          ) : (
            /* Plans Step */
            <div className="p-6 bg-gray-50/50">
              <button
                onClick={() => setCurrentStep('welcome')}
                className="flex items-center gap-2 text-gray-400 hover:text-bulky-navy-600 mb-6 transition-colors group"
              >
                <span className="material-icons-round text-sm group-hover:-translate-x-1 transition-transform">arrow_back</span>
                <span className="text-[10px] font-normal uppercase tracking-wider">Go Back</span>
              </button>

              {packagesLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-10 h-10 border-[3px] border-bulky-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-[10px] text-gray-400 font-normal uppercase tracking-wider">Loading Plans...</p>
                </div>
              ) : packagesError ? (
                <div className="text-center py-12 text-red-500 uppercase tracking-wider text-[10px]">Error: {packagesError.message}</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {packagesData?.availablePackages?.map((pkg) => {
                    // Check if this is the current active plan
                    const activeSubscription = currentSubscriptionData?.mySubscriptions?.find(sub => sub.status === 'active');
                    const isCurrentPlan = activeSubscription?.package?.id === pkg.id;
                    
                    return (
                    <div
                      key={pkg.id}
                      className={`bg-white border rounded-xl p-5 transition-all duration-300 relative group overflow-hidden ${
                        isCurrentPlan 
                          ? 'border-green-500 shadow-xl shadow-green-500/10 ring-2 ring-green-500/20'
                          : selectedPlan?.id === pkg.id
                          ? 'border-bulky-blue-500 shadow-xl shadow-bulky-blue-500/10'
                          : 'border-gray-100 shadow-sm hover:shadow-lg hover:border-bulky-blue-200'
                        }`}
                    >
                      {/* Current Plan Badge */}
                      {isCurrentPlan && (
                        <div className="absolute top-3 left-3 bg-green-500 text-white px-2 py-0.5 rounded text-[8px] font-normal uppercase tracking-wider flex items-center gap-1">
                          <span className="material-icons-round text-xs">check_circle</span>
                          Current Plan
                        </div>
                      )}
                      
                      {/* Discount Badge */}
                      {pkg.discount_percentage > 0 && (
                        <div className={`absolute top-3 ${isCurrentPlan ? 'right-3' : 'right-3'} bg-emerald-500 text-white px-2 py-0.5 rounded text-[8px] font-normal uppercase tracking-wider`}>
                          -{pkg.discount_percentage}%
                        </div>
                      )}

                      {/* Plan Header */}
                      <div className="mb-4">
                        <div className="text-[9px] font-normal text-bulky-blue-500 uppercase tracking-[0.2em] mb-1">{pkg.country || 'ALL COUNTRIES'}</div>
                        <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight transition-colors group-hover:text-bulky-blue-600">{pkg.name}</h2>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-xl font-normal text-bulky-navy-800 uppercase tracking-tighter">₹{pkg.price}</span>
                          {pkg.discount_percentage > 0 && (
                            <span className="text-[10px] font-normal text-gray-300 line-through">
                              ₹{(pkg.price / (1 - pkg.discount_percentage / 100)).toFixed(0)}
                            </span>
                          )}
                          <span className="text-[9px] font-normal text-gray-400 uppercase tracking-wider ml-1">/ {pkg.duration_days}D</span>
                        </div>
                      </div>

                      {/* Features Grid */}
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-6 border-t border-gray-50 pt-4">
                        <div className="flex flex-col">
                          <span className="text-[8px] font-normal text-gray-400 uppercase tracking-wider">Staff</span>
                          <span className="text-[10px] font-normal text-bulky-navy-700 uppercase tracking-tight">{pkg.max_staff_members}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-normal text-gray-400 uppercase tracking-wider">Contacts</span>
                          <span className="text-[10px] font-normal text-bulky-navy-700 uppercase tracking-tight">{pkg.max_contacts?.toLocaleString()}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-normal text-gray-400 uppercase tracking-wider">WhatsApp Numbers</span>
                          <span className="text-[10px] font-normal text-bulky-navy-700 uppercase tracking-tight">{pkg.max_whatsapp_instances}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-normal text-gray-400 uppercase tracking-wider">Daily Messages</span>
                          <span className="text-[10px] font-normal text-bulky-navy-700 uppercase tracking-tight">{pkg.max_bulk_messages_per_day}</span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-2">
                        {/* Purchase/Renew Button */}
                        <button
                          onClick={() => handlePlanSelect(pkg)}
                          disabled={isProcessing}
                          className={`w-full btn-primary py-2.5 !rounded-xl !text-[10px] !tracking-wider ${
                            isCurrentPlan 
                              ? '!bg-green-600 hover:!bg-green-700' 
                              : isProcessing && selectedPlan?.id === pkg.id 
                              ? 'opacity-50 !bg-gray-400 cursor-not-allowed' 
                              : ''
                            }`}
                        >
                          {isProcessing && selectedPlan?.id === pkg.id ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Initializing Payment...</span>
                            </>
                          ) : isCurrentPlan ? (
                            <>
                              <span className="material-icons-round text-sm">autorenew</span>
                              <span>Renew This Plan</span>
                            </>
                          ) : (
                            <>
                              <span className="material-icons-round text-sm">electric_bolt</span>
                              <span>Buy This Plan</span>
                            </>
                          )}
                        </button>

                        {/* More Information Button */}
                        <button
                          onClick={() => {
                            setSelectedPackageForDetails(pkg);
                            setShowPackageDetails(true);
                          }}
                          className="w-full bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-bulky-blue-600 py-2.5 rounded-xl text-[10px] font-normal uppercase tracking-wider transition-all flex items-center justify-center gap-2 border border-gray-100 hover:border-bulky-blue-200"
                        >
                          <span className="material-icons-round text-sm">info</span>
                          <span>More Information</span>
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}

              {error && (
                <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3">
                  <span className="material-icons-round text-red-500">error</span>
                  <p className="text-[10px] font-normal text-red-600 uppercase tracking-wider leading-relaxed">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Package Details Modal */}
      {showPackageDetails && selectedPackageForDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-bulky-blue-500 to-bulky-blue-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                    <span className="material-icons-round text-2xl">info</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold tracking-tight">{selectedPackageForDetails.name}</h2>
                    <p className="text-blue-100 text-sm mt-1">Full Plan Details</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowPackageDetails(false);
                    setSelectedPackageForDetails(null);
                  }}
                  className="w-10 h-10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all"
                >
                  <span className="material-icons-round text-xl">close</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Price Section */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 mb-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-3xl font-bold text-bulky-navy-800">₹{selectedPackageForDetails.price}</span>
                    {selectedPackageForDetails.discount_percentage > 0 && (
                      <>
                        <span className="text-lg text-gray-400 line-through">
                          ₹{(selectedPackageForDetails.price / (1 - selectedPackageForDetails.discount_percentage / 100)).toFixed(0)}
                        </span>
                        <span className="bg-green-500 text-white px-2 py-1 rounded text-xs font-medium">
                          -{selectedPackageForDetails.discount_percentage}% OFF
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm">Valid for {selectedPackageForDetails.duration_days} days</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Daily cost: ₹{(selectedPackageForDetails.price / selectedPackageForDetails.duration_days).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Plan Features */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="material-icons-round text-blue-600">settings</span>
                  What You Get
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="material-icons-round text-blue-600">groups</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Staff Members</h4>
                        <p className="text-xs text-gray-500">Team management capacity</p>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-bulky-navy-800">{selectedPackageForDetails.max_staff_members}</div>
                    <p className="text-xs text-gray-600 mt-1">Maximum staff accounts you can create and manage</p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <span className="material-icons-round text-green-600">contacts</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Contacts</h4>
                        <p className="text-xs text-gray-500">Customer database limit</p>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-bulky-navy-800">{selectedPackageForDetails.max_contacts?.toLocaleString()}</div>
                    <p className="text-xs text-gray-600 mt-1">Total contacts you can store and manage</p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <span className="material-icons-round text-purple-600">phone_android</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">WhatsApp Instances</h4>
                        <p className="text-xs text-gray-500">Connected phone numbers</p>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-bulky-navy-800">{selectedPackageForDetails.max_whatsapp_instances}</div>
                    <p className="text-xs text-gray-600 mt-1">WhatsApp Business accounts you can connect</p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <span className="material-icons-round text-orange-600">send</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Daily Messages</h4>
                        <p className="text-xs text-gray-500">Bulk messaging limit</p>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-bulky-navy-800">{selectedPackageForDetails.max_bulk_messages_per_day?.toLocaleString()}</div>
                    <p className="text-xs text-gray-600 mt-1">Messages you can send per day via bulk campaigns</p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <span className="material-icons-round text-indigo-600">folder</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Contact Groups</h4>
                        <p className="text-xs text-gray-500">Organization segments</p>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-bulky-navy-800">{selectedPackageForDetails.max_contact_groups}</div>
                    <p className="text-xs text-gray-600 mt-1">Contact groups you can create for targeted messaging</p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                        <span className="material-icons-round text-pink-600">description</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Templates</h4>
                        <p className="text-xs text-gray-500">Message templates</p>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-bulky-navy-800">{selectedPackageForDetails.max_templates}</div>
                    <p className="text-xs text-gray-600 mt-1">Custom message templates you can create and save</p>
                  </div>
                </div>
              </div>

              {/* Plan Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="material-icons-round text-green-600">verified_user</span>
                  Plan Information
                </h3>
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Plan Type */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Plan Type</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Package Type</span>
                          <span className="text-sm font-medium text-gray-900 capitalize bg-white px-2 py-1 rounded">
                            {selectedPackageForDetails.package_type || 'Standard'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Country</span>
                          <span className="text-sm font-medium text-gray-900 bg-white px-2 py-1 rounded">
                            {selectedPackageForDetails.country === 'IN' ? 'India' : selectedPackageForDetails.country || 'Global'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Status</span>
                          <span className={`text-sm font-medium px-2 py-1 rounded ${selectedPackageForDetails.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {selectedPackageForDetails.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Support & Availability */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Support & Availability</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Priority Support</span>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${selectedPackageForDetails.priority_support ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            <span className="text-sm font-medium text-gray-900">
                              {selectedPackageForDetails.priority_support ? 'Enabled' : 'Standard'}
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Availability</span>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span className="text-sm font-medium text-gray-900">24/7 Available</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Response Time</span>
                          <span className="text-sm font-medium text-gray-900 bg-white px-2 py-1 rounded">
                            {selectedPackageForDetails.priority_support ? '< 2 hours' : '< 24 hours'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Plan Summary */}
                  <div className="mt-6 pt-6 border-t border-green-200">
                    <h4 className="font-medium text-gray-900 mb-3">Plan Summary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-700">{selectedPackageForDetails.max_staff_members}</div>
                        <div className="text-xs text-gray-600">Staff Members</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-700">{(selectedPackageForDetails.max_contacts / 1000).toFixed(0)}K</div>
                        <div className="text-xs text-gray-600">Contacts</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-700">{selectedPackageForDetails.max_whatsapp_instances}</div>
                        <div className="text-xs text-gray-600">WhatsApp Numbers</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-700">{(selectedPackageForDetails.max_bulk_messages_per_day / 1000).toFixed(0)}K</div>
                        <div className="text-xs text-gray-600">Daily Messages</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="material-icons-round text-orange-600">description</span>
                  Additional Details
                </h3>
                <div className="bg-orange-50 rounded-xl p-6">
                  {selectedPackageForDetails.notes ? (
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                      <p className="text-sm text-gray-700 leading-relaxed">{selectedPackageForDetails.notes}</p>
                    </div>
                  ) : null}
                  
                  {selectedPackageForDetails.details ? (
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-900 mb-2">Plan Details</h4>
                      <p className="text-sm text-gray-700 leading-relaxed">{selectedPackageForDetails.details}</p>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Terms and Conditions */}
              {selectedPackageForDetails.terms_and_conditions && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="material-icons-round text-red-600">gavel</span>
                    Terms and Conditions
                  </h3>
                  <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                    <div 
                      className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: selectedPackageForDetails.terms_and_conditions }}
                    />
                  </div>
                </div>
              )}

              {/* Features & Capabilities */}
              {selectedPackageForDetails.features && Object.keys(selectedPackageForDetails.features).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="material-icons-round text-purple-600">extension</span>
                    Features & Capabilities
                  </h3>
                  <div className="bg-purple-50 rounded-xl p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(selectedPackageForDetails.features).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between bg-white rounded-lg p-3">
                          <span className="text-sm font-medium text-gray-900 capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className={`text-sm px-2 py-1 rounded ${
                            value === true || value === 'enabled' ? 'bg-green-100 text-green-800' : 
                            value === false || value === 'disabled' ? 'bg-red-100 text-red-800' : 
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {typeof value === 'boolean' ? (value ? 'Enabled' : 'Disabled') : value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Plan Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="material-icons-round text-gray-600">info</span>
                  Plan Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Plan Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Plan ID</span>
                        <span className="text-sm font-mono text-gray-900">#{selectedPackageForDetails.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Currency</span>
                        <span className="text-sm font-medium text-gray-900">{selectedPackageForDetails.currency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Duration</span>
                        <span className="text-sm font-medium text-gray-900">{selectedPackageForDetails.duration_days} days</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="font-medium text-gray-900 mb-2">System Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Created</span>
                        <span className="text-sm text-gray-900">
                          {selectedPackageForDetails.created_at ? new Date(selectedPackageForDetails.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Last Updated</span>
                        <span className="text-sm text-gray-900">
                          {selectedPackageForDetails.updated_at ? new Date(selectedPackageForDetails.updated_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className={`text-sm font-medium px-2 py-1 rounded ${selectedPackageForDetails.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {selectedPackageForDetails.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPackageDetails(false);
                    setSelectedPackageForDetails(null);
                    handlePlanSelect(selectedPackageForDetails);
                  }}
                  className="flex-1 bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-medium py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <span className="material-icons-round text-sm">electric_bolt</span>
                  Purchase This Plan
                </button>
                <button
                  onClick={() => {
                    setShowPackageDetails(false);
                    setSelectedPackageForDetails(null);
                  }}
                  className="px-6 py-3 border border-gray-300 text-gray-600 hover:text-gray-800 hover:border-gray-400 rounded-xl transition-all duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default SubscriptionPlans;